/**
 * Challenges API Service
 * This service handles communication with the Azure Functions backend for challenges functionality.
 * (Previously called certificates, now renamed to challenges)
 */

import axios from 'axios';

class ChallengesService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';
    // Ensure baseURL doesn't end with slash to avoid double slashes
    this.baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    console.log('ChallengesService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get all challenges for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response with challenges
   */
  async getUserChallenges(userId) {
    try {
      const url = `${this.baseURL}/challenges/get?userId=${userId}`;
      console.log(`Getting challenges from: ${url}`);
      
      const response = await axios.get(url);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error fetching challenges:', error);
      throw error;
    }
  }

  /**
   * Generate a challenge completion certificate
   * @param {Object} challengeData - Challenge data object
   * @returns {Promise} - API response
   */
  async generateChallenge(challengeData) {
    try {
      const url = `${this.baseURL}/challenges/post`;
      console.log(`Generating challenge: ${url}`, challengeData);
      
      const response = await axios.post(url, challengeData);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error generating challenge:', error);
      throw error;
    }
  }

  /**
   * Update a challenge
   * @param {string} challengeId - Challenge ID
   * @param {Object} updateData - Data to update
   * @returns {Promise} - API response
   */
  async updateChallenge(challengeId, updateData) {
    try {
      const url = `${this.baseURL}/challenges/update?id=${challengeId}`;
      console.log(`Updating challenge: ${url}`, updateData);
      
      const response = await axios.put(url, updateData);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error updating challenge:', error);
      throw error;
    }
  }

  /**
   * Delete a challenge
   * @param {string} challengeId - Challenge ID
   * @returns {Promise} - API response
   */
  async deleteChallenge(challengeId) {
    try {
      const url = `${this.baseURL}/challenges/delete?id=${challengeId}`;
      console.log(`Deleting challenge: ${url}`);
      
      const response = await axios.delete(url);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error deleting challenge:', error);
      throw error;
    }
  }

  /**
   * Delete all challenges for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async deleteAllUserChallenges(userId) {
    try {
      const url = `${this.baseURL}/challenges/all/delete?userId=${userId}`;
      console.log(`Deleting all challenges for user: ${url}`);
      
      const response = await axios.delete(url);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error deleting all challenges:', error);
      throw error;
    }
  }

  /**
   * Get all challenges (admin only)
   * @returns {Promise} - API response with all challenges
   */
  async getAllChallenges() {
    try {
      const url = `${this.baseURL}/challenges/get`;
      console.log(`Getting all challenges from: ${url}`);
      
      const response = await axios.get(url);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error fetching all challenges:', error);
      throw error;
    }
  }

  /**
   * Check if user has completed a specific challenge
   * @param {string} userId - User ID
   * @param {string} challengeType - Type of challenge (e.g., 'course_completion', 'video_streak')
   * @returns {Promise} - Boolean indicating completion status
   */
  async hasCompletedChallenge(userId, challengeType) {
    try {
      const challenges = await this.getUserChallenges(userId);
      const userChallenges = challenges.data || [];
      
      return userChallenges.some(challenge => 
        challenge.type === challengeType && challenge.status === 'completed'
      );
    } catch (error) {
      console.error('Error checking challenge completion:', error);
      return false;
    }
  }

  /**
   * Get challenge statistics for a user
   * @param {string} userId - User ID
   * @returns {Promise} - Challenge statistics
   */
  async getChallengeStats(userId) {
    try {
      const challenges = await this.getUserChallenges(userId);
      const userChallenges = challenges.data || [];
      
      const stats = {
        total: userChallenges.length,
        completed: userChallenges.filter(c => c.status === 'completed').length,
        inProgress: userChallenges.filter(c => c.status === 'in_progress').length,
        pending: userChallenges.filter(c => c.status === 'pending').length
      };
      
      stats.completionRate = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;
      
      return { data: stats, message: "Statistics calculated successfully" };
    } catch (error) {
      console.error('Error calculating challenge statistics:', error);
      return { 
        data: { total: 0, completed: 0, inProgress: 0, pending: 0, completionRate: 0 }, 
        message: "Error calculating statistics" 
      };
    }
  }
}

// Export a singleton instance
const challengesService = new ChallengesService();
export default challengesService;
